import { Button } from "@/components/ui/button";
import { downloadRunPdf } from "@/services/api";
import { pdfExportMessages } from "@/services/messages";
import { useMutation } from "@tanstack/react-query";
import { Download, Loader2 } from "lucide-react";
import { useCallback, useState } from "react";

interface DownloadPdfButtonProps {
	runId: string;
	/** Текущий статус прогона. Кнопка активна только для `completed`. */
	runStatus: "pending" | "running" | "completed" | "failed";
}

/**
 * Кнопка «Скачать PDF» (фича 034, US1).
 *
 * Поведение (FR-001 / FR-002 / FR-003 / FR-018 / FR-019a):
 * - доступна только при `runStatus === "completed"`; иначе disabled с tooltip;
 * - при клике — fetch PDF, кнопка disabled + спиннер, страница не двигается;
 * - при успехе — auto-download через `URL.createObjectURL` + `<a download>`;
 * - при ошибке — inline-сообщение с кнопкой «Повторить» (toast'а в проекте
 *   нет, используем компактный inline-блок справа от кнопки).
 */
export function DownloadPdfButton({ runId, runStatus }: DownloadPdfButtonProps) {
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const isCompleted = runStatus === "completed";

	const mutation = useMutation({
		mutationFn: () => downloadRunPdf(runId),
		onMutate: () => {
			setErrorMessage(null);
		},
		onSuccess: ({ blob, filename }) => {
			triggerBrowserDownload(blob, filename);
		},
		onError: (err: unknown) => {
			const msg =
				err instanceof Error && err.message
					? err.message
					: pdfExportMessages.error_generation_failed;
			setErrorMessage(msg);
		},
	});

	const handleClick = useCallback(() => {
		if (!isCompleted || mutation.isPending) return;
		mutation.mutate();
	}, [isCompleted, mutation]);

	const tooltip = !isCompleted ? pdfExportMessages.button_tooltip_disabled : undefined;
	const inFlight = mutation.isPending;

	return (
		<div className="inline-flex items-center gap-2">
			<Button
				type="button"
				variant="outline"
				size="sm"
				disabled={!isCompleted || inFlight}
				title={tooltip}
				onClick={handleClick}
				aria-label={pdfExportMessages.button_label}
			>
				{inFlight ? (
					<Loader2 className="size-4 animate-spin" aria-hidden="true" />
				) : (
					<Download className="size-4" aria-hidden="true" />
				)}
				<span>{pdfExportMessages.button_label}</span>
			</Button>
			{errorMessage ? (
				<div
					role="alert"
					className="inline-flex items-center gap-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700"
				>
					<span>{errorMessage}</span>
					{/* M7 (code-review): retry-кнопка дублирует основную и должна
					    блокироваться во время in-flight, иначе спам-клик после ошибки
					    наплодит N параллельных mutation'ов. */}
					<button
						type="button"
						onClick={handleClick}
						disabled={inFlight}
						className="font-medium underline underline-offset-2 hover:text-red-900 disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
					>
						{pdfExportMessages.toast_retry}
					</button>
				</div>
			) : null}
		</div>
	);
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	// Safari: ссылка должна быть в DOM перед click.
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	// Минимальный delay для совместимости со старыми браузерами
	// (revoke сразу после click может прервать загрузку).
	setTimeout(() => URL.revokeObjectURL(url), 1000);
}
